// Spec s7: the extraction contract, and the validation that stands between a
// model response and the panel.
//
// Nothing from a response reaches the panel without passing through
// `validateExtraction`. Unknown ids are dropped, missing ids default to empty,
// confidence is clamped, and a "filled" whose evidence is not actually in the
// transcript is demoted to "uncertain" - the prompt asks for a verbatim span
// precisely so this check is possible.

import type { ChecklistField } from '../config/types';

export type FieldStatus = 'empty' | 'uncertain' | 'filled';

/**
 * A value the person on the call asserted, by confirming the model's guess or
 * typing one in.
 *
 * Held separately from extraction results on purpose. Spec s5.3 has each pass
 * recompute the whole answer and replace panel state wholesale, which is what
 * makes mid-call corrections work without reconciliation logic - but it would
 * also wipe a human answer every seven seconds. So the model's output stays
 * disposable and this layer sits on top of it. Never merged into the model's
 * result, never sent back to the model.
 */
export type FieldOverride = {
  value: string;
  /** How it got here, so the panel can say. */
  source: 'confirmed' | 'typed';
};

export type FieldState = {
  id: string;
  label: string;
  status: FieldStatus;
  value: string | null;
  confidence: number;
  evidence: string | null;
  /** False when the model's span could not be located in the transcript. */
  evidenceVerified: boolean;
  /** Set when this value came from the person on the call, not the model. */
  fromUser?: boolean;
};

/**
 * Lay a human answer over the model's, for one field.
 *
 * The model's evidence is kept only when it still supports the value shown -
 * quoting a transcript span underneath a value the user typed instead would be
 * a lie about where the answer came from.
 */
/** A field the model has said nothing about yet. */
export function blank(field: { id: string; label: string }): FieldState {
  return {
    id: field.id,
    label: field.label,
    status: 'empty',
    value: null,
    confidence: 0,
    evidence: null,
    evidenceVerified: true,
  };
}

/**
 * What the checklist actually says right now: one entry per configured field,
 * the model's output where it has any, and a person's answer layered over that.
 *
 * The panel draws this and the saved record of the call stores it, which is the
 * point of it being one function - a stored answer that disagreed with what the
 * inspector was looking at would be worse than no record at all.
 */
export function mergedFields(
  fields: readonly { id: string; label: string }[],
  state: readonly FieldState[],
  overrides: ReadonlyMap<string, FieldOverride>,
): FieldState[] {
  const extracted = new Map(state.map((entry) => [entry.id, entry]));
  return fields.map((field) =>
    applyOverride(extracted.get(field.id) ?? blank(field), overrides.get(field.id)),
  );
}

export function applyOverride(
  entry: FieldState,
  override: FieldOverride | undefined,
): FieldState {
  if (!override) return entry;
  const sameValue = override.value === entry.value;
  return {
    ...entry,
    status: 'filled',
    value: override.value,
    confidence: 1,
    evidence: sameValue ? entry.evidence : null,
    evidenceVerified: sameValue ? entry.evidenceVerified : true,
    fromUser: true,
  };
}

export type RawFieldResult = {
  id?: unknown;
  status?: unknown;
  value?: unknown;
  confidence?: unknown;
  evidence?: unknown;
};

export const MAX_EVIDENCE_CHARS = 200;

const STATUSES: FieldStatus[] = ['empty', 'uncertain', 'filled'];

function emptyState(field: ChecklistField): FieldState {
  return {
    id: field.id,
    label: field.label,
    status: 'empty',
    value: null,
    confidence: 0,
    evidence: null,
    evidenceVerified: true,
  };
}

function normalizeForSearch(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Punctuation and casing differ between the span and the transcript; content does not. */
function spanAppearsIn(span: string, transcript: string): boolean {
  const needle = normalizeForSearch(span);
  if (!needle) return false;
  return normalizeForSearch(transcript).includes(needle);
}

function coerceValue(field: ChecklistField, raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text) return null;

  switch (field.type) {
    case 'number': {
      const digits = text.replace(/[^0-9.\-]/g, '');
      return digits && Number.isFinite(Number(digits)) ? digits : null;
    }
    case 'boolean': {
      const lowered = text.toLowerCase();
      if (['true', 'yes', 'y'].includes(lowered)) return 'true';
      if (['false', 'no', 'n'].includes(lowered)) return 'false';
      return null;
    }
    case 'enum': {
      const match = (field.enumValues ?? []).find(
        (option) => option.toLowerCase() === text.toLowerCase(),
      );
      return match ?? null;
    }
    default:
      return text;
  }
}

function clampConfidence(raw: unknown): number {
  const value = Number(raw);
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

/**
 * Turn a model response into panel state, in config order.
 * Never throws: a malformed response degrades to empty rows, it does not break
 * the call UI.
 */
export function validateExtraction(
  fields: ChecklistField[],
  raw: unknown,
  transcript: string,
): FieldState[] {
  const byId = new Map<string, RawFieldResult>();
  const payload = raw as { fields?: unknown } | null;
  if (payload && Array.isArray(payload.fields)) {
    for (const entry of payload.fields as RawFieldResult[]) {
      if (entry && typeof entry.id === 'string') byId.set(entry.id, entry);
    }
    // Unknown ids are simply never looked up, which is how they get dropped.
  }

  return fields.map((field) => {
    const result = byId.get(field.id);
    if (!result) return emptyState(field); // missing ids default to empty

    let status: FieldStatus = STATUSES.includes(result.status as FieldStatus)
      ? (result.status as FieldStatus)
      : 'empty';

    const evidenceRaw =
      typeof result.evidence === 'string' && result.evidence.trim()
        ? result.evidence.trim().slice(0, MAX_EVIDENCE_CHARS)
        : null;

    let value = status === 'empty' ? null : coerceValue(field, result.value);
    // A value that does not survive coercion is not a clear, unambiguous value.
    if (status === 'filled' && value === null) status = 'uncertain';
    if (status === 'uncertain' && value === null && !evidenceRaw) status = 'empty';

    const evidenceVerified = evidenceRaw ? spanAppearsIn(evidenceRaw, transcript) : false;

    // Spec s7: no span, not filled. Bias toward uncertain - a false "you already
    // have this" is worse than a false "you still need this".
    if (status === 'filled' && !evidenceVerified) status = 'uncertain';
    if (status === 'empty') value = null;

    return {
      id: field.id,
      label: field.label,
      status,
      value,
      confidence: clampConfidence(result.confidence),
      evidence: status === 'empty' ? null : evidenceRaw,
      evidenceVerified,
    };
  });
}
